import React, { useMemo } from 'react'
import { Box, Chip, Stack, Typography } from '@mui/material'
import { getComputedWidth, isObject } from '@/components/common/utils'
import { formatTenantTimezoneLabel } from '@/utils/tenant'

export const ChipSelector = ({
  id,
  title = '',
  chips = [],
  value,
  onChange,
  setValue,
  width,
  fullWidth = true,
  readOnly = false,
  returnObject = false,
}) => {
  const computedWidth = getComputedWidth(width, fullWidth)
  const timezoneLabel = formatTenantTimezoneLabel()

  const valueId = useMemo(() => {
    if (!value) return undefined
    return isObject(value) ? value.id : value
  }, [value])

  const handleSelect = chip => {
    if (readOnly) return
    const payload = returnObject ? chip : chip.id
    if (setValue) setValue(payload)
    if (onChange) onChange(payload)
  }

  return (
    <Box
      id={id}
      data-testid={id}
      sx={{
        display: 'flex',
        gap: { xs: 1, md: 2 },
        flexWrap: 'wrap',
        width: computedWidth,
        padding: { xs: 1.5, md: 2.5 },
        borderRadius: 2,
        backgroundColor: 'secondary.main',
        justifyContent: 'flex-start',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: { xs: 'stretch', md: 'center' },
      }}
    >
      {title ? (
        <Typography
          variant='h6'
          component='div'
          sx={{
            minWidth: { xs: 'auto', md: '140px' },
            fontSize: { xs: '0.875rem', md: '1.25rem' },
            fontWeight: { xs: 500, md: 400 },
            lineHeight: { xs: 1.4, md: 1.5 },
            whiteSpace: 'nowrap',
          }}
        >
          {title}
          {timezoneLabel ? (
            <Typography
              component='span'
              sx={{
                ml: 0.75,
                fontSize: { xs: '0.75rem', md: '0.875rem' },
                fontWeight: 400,
                color: 'text.secondary',
              }}
            >
              {timezoneLabel}
            </Typography>
          ) : null}
        </Typography>
      ) : null}
      <Stack
        direction='row'
        sx={{
          flexWrap: 'wrap',
          gap: { xs: 0.75, md: 1 },
          width: { xs: '100%', md: 'auto' },
        }}
      >
        {chips.map(chip => {
          const isActive = valueId === chip.id
          const isChipDisabled = readOnly || chip.disabled || false
          return (
            <Chip
              key={chip.id}
              id={`${id}-chip-${chip.id}`}
              data-testid={`${id}-chip-${chip.id}`}
              color={isActive ? 'primary' : 'default'}
              variant={isActive ? 'filled' : 'outlined'}
              onClick={() => handleSelect(chip)}
              disabled={isChipDisabled}
              sx={{
                height: { xs: '40px', md: '44px' },
                borderRadius: '25px',
                width: { xs: '100%', md: '160px' },
                '& .MuiChip-label': {
                  width: '100%',
                  padding: { xs: '0 12px', md: '0 16px' },
                },
              }}
              label={
                <Box
                  sx={{
                    display: 'flex',
                    textAlign: { xs: 'left', md: 'center' },
                    flexDirection: { xs: 'row', md: 'column' },
                    alignItems: { xs: 'center', md: 'initial' },
                    justifyContent: { xs: 'space-between', md: 'flex-start' },
                  }}
                >
                  <Typography
                    variant='body2'
                    sx={{
                      fontWeight: 500,
                      fontSize: { xs: '0.75rem', md: '0.875rem' },
                      lineHeight: { xs: 1.3, md: 1.5 },
                    }}
                  >
                    {chip.text1}
                  </Typography>
                  {chip.text2 ? (
                    <Typography
                      variant='caption'
                      sx={{
                        fontSize: { xs: '0.7rem', md: '0.75rem' },
                        lineHeight: { xs: 1.2, md: 1.4 },
                      }}
                    >
                      {chip.text2}
                    </Typography>
                  ) : null}
                </Box>
              }
            />
          )
        })}
      </Stack>
    </Box>
  )
}
